"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import { type FC, memo, useState, useMemo } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";
import { ResourceLink, isResourceLink, extractResourceId } from "./resource-link";
import { MermaidDiagram } from "./mermaid-diagram";

/**
 * MarkdownTextPrimitive wrapper for use within assistant-ui Thread context
 */
const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md max-w-full overflow-hidden break-words [overflow-wrap:anywhere]"
      components={defaultComponents}
      componentsByLanguage={{
        mermaid: {
          SyntaxHighlighter: MermaidDiagram,
        },
      }}
    />
  );
};

export const MarkdownTextPrimitiveWrapper = memo(MarkdownTextImpl);

/**
 * Standalone markdown renderer that accepts text prop
 * Supports mermaid diagrams via code block detection
 */
interface MarkdownTextProps {
  text: string;
  className?: string;
}

const MarkdownTextStandalone: FC<MarkdownTextProps> = ({ text, className }) => {
  // Parse markdown and detect mermaid blocks
  const parts = useMemo(() => {
    const result: Array<{ type: 'text' | 'mermaid'; content: string }> = [];

    // Split by mermaid code blocks
    const mermaidRegex = /```mermaid\n([\s\S]*?)```/g;
    let lastIndex = 0;
    let match;

    while ((match = mermaidRegex.exec(text)) !== null) {
      // Add text before the mermaid block
      if (match.index > lastIndex) {
        result.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      // Add the mermaid block
      result.push({ type: 'mermaid', content: match[1].trim() });
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      result.push({ type: 'text', content: text.slice(lastIndex) });
    }

    return result;
  }, [text]);

  return (
    <div className={cn("aui-md max-w-full overflow-hidden break-words [overflow-wrap:anywhere]", className)}>
      {parts.map((part, idx) => {
        if (part.type === 'mermaid') {
          return <MermaidDiagram key={idx} code={part.content} />;
        }
        // For text parts, render with basic formatting
        return <SimpleMarkdownText key={idx} text={part.content} />;
      })}
    </div>
  );
};

/**
 * Full markdown text renderer using react-markdown with GFM support.
 * Handles tables, headings, lists, code blocks, links, etc.
 */
const SimpleMarkdownText: FC<{ text: string }> = ({ text }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ className, ...props }) => (
          <h1 className={cn("aui-md-h1 mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0", className)} {...props} />
        ),
        h2: ({ className, ...props }) => (
          <h2 className={cn("aui-md-h2 mt-8 mb-4 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
        ),
        h3: ({ className, ...props }) => (
          <h3 className={cn("aui-md-h3 mt-6 mb-4 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
        ),
        h4: ({ className, ...props }) => (
          <h4 className={cn("aui-md-h4 mt-6 mb-4 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
        ),
        p: ({ className, ...props }) => (
          <p className={cn("aui-md-p mt-5 mb-5 leading-7 first:mt-0 last:mb-0 break-words", className)} {...props} />
        ),
        a: ({ className, ...props }) => (
          <a className={cn("aui-md-a font-medium text-primary underline underline-offset-4", className)} {...props} />
        ),
        ul: ({ className, ...props }) => (
          <ul className={cn("aui-md-ul my-5 ml-6 list-disc [&>li]:mt-2", className)} {...props} />
        ),
        ol: ({ className, ...props }) => (
          <ol className={cn("aui-md-ol my-5 ml-6 list-decimal [&>li]:mt-2", className)} {...props} />
        ),
        blockquote: ({ className, ...props }) => (
          <blockquote className={cn("aui-md-blockquote border-l-2 pl-6 italic", className)} {...props} />
        ),
        hr: ({ className, ...props }) => (
          <hr className={cn("aui-md-hr my-5 border-b", className)} {...props} />
        ),
        table: ({ className, ...props }) => (
          <table className={cn("aui-md-table my-5 w-full border-separate border-spacing-0 overflow-y-auto", className)} {...props} />
        ),
        th: ({ className, ...props }) => (
          <th className={cn("aui-md-th bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right", className)} {...props} />
        ),
        td: ({ className, ...props }) => (
          <td className={cn("aui-md-td border-b border-l px-4 py-2 text-left last:border-r [&[align=center]]:text-center [&[align=right]]:text-right", className)} {...props} />
        ),
        tr: ({ className, ...props }) => (
          <tr className={cn("aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg", className)} {...props} />
        ),
        pre: ({ className, ...props }) => (
          <pre className={cn("aui-md-pre overflow-x-auto rounded-lg bg-black p-4 text-white max-w-full my-2", className)} {...props} />
        ),
        code: ({ className, ...props }) => (
          <code className={cn("aui-md-inline-code rounded border bg-muted font-semibold", className)} {...props} />
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

export const MarkdownText = memo(MarkdownTextStandalone);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-4 flex items-center justify-between gap-4 rounded-t-lg bg-muted-foreground/15 px-4 py-2 text-sm font-semibold text-foreground dark:bg-muted-foreground/20">
      <span className="aui-code-header-language lowercase [&>span]:text-xs">
        {language}
      </span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-8 mb-4 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-6 mb-4 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-6 mb-4 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 my-4 text-lg font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 my-4 font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p mt-5 mb-5 leading-7 first:mt-0 last:mb-0 break-words",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, href, children, ...props }) => {
    // Intercept /resources/* links and render ResourceLink component
    if (isResourceLink(href)) {
      const resourceId = extractResourceId(href!);
      return (
        <ResourceLink
          resourceId={resourceId}
          className={cn("aui-md-a", className)}
        >
          {children}
        </ResourceLink>
      );
    }
    // Regular links
    return (
      <a
        href={href}
        className={cn(
          "aui-md-a font-medium text-primary underline underline-offset-4",
          className,
        )}
        {...props}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("aui-md-blockquote border-l-2 pl-6 italic", className)}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("aui-md-ul my-5 ml-6 list-disc [&>li]:mt-2", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("aui-md-ol my-5 ml-6 list-decimal [&>li]:mt-2", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("aui-md-hr my-5 border-b", className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-5 w-full border-separate border-spacing-0 overflow-y-auto",
        className,
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-b border-l px-4 py-2 text-left last:border-r [&[align=center]]:text-center [&[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "aui-md-pre overflow-x-auto !rounded-t-none rounded-b-lg bg-black p-4 text-white max-w-full",
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            "aui-md-inline-code rounded border bg-muted font-semibold break-all",
          isCodeBlock && "block overflow-x-auto",
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});
